import React from 'react';
import Navbar from '../components/Navbar';
import SEOHead from '../components/SEOHead';
import { ShoppingCartIcon, CurrencyDollarIcon, CloudIcon } from '@heroicons/react/24/outline';

const UseCases = () => {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-950">
      <SEOHead
        fullTitle="TypoGuard Use Cases | Domain Protection for Industries"
        description="Discover how TypoGuard protects e-commerce, crypto, and SaaS platforms from typosquatting and brand impersonation."
      />
      
      <Navbar />

      <div className="pt-24 pb-16 px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <h1 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-5xl mb-6">
              Industry Use Cases
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Every industry faces unique threats. See how TypoGuard's automated detection safeguards your specific business model.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* E-Commerce */}
            <div className="bg-white dark:bg-dark-800 rounded-2xl p-8 shadow-sm border border-gray-200 dark:border-dark-600 hover:shadow-lg transition-shadow duration-300">
              <div className="flex items-center justify-center w-14 h-14 bg-blue-100 dark:bg-blue-900/30 rounded-xl mb-6">
                <ShoppingCartIcon className="h-8 w-8 text-blue-600 dark:text-blue-400" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">E-Commerce & Retail</h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
                Counterfeiters use misspelled domains to sell fake goods or steal credit card information from your customers. TypoGuard helps you identify and take down these fraudulent storefronts before they damage your brand reputation.
              </p>
              <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span> Protect customer payment data
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span> Prevent brand dilution from counterfeits
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span> Maintain SEO ranking
                </li>
              </ul>
            </div>

            {/* Crypto / Finance */}
            <div className="bg-white dark:bg-dark-800 rounded-2xl p-8 shadow-sm border border-gray-200 dark:border-dark-600 hover:shadow-lg transition-shadow duration-300">
              <div className="flex items-center justify-center w-14 h-14 bg-orange-100 dark:bg-orange-900/30 rounded-xl mb-6">
                <CurrencyDollarIcon className="h-8 w-8 text-orange-600 dark:text-orange-400" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Crypto & Finance</h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
                Phishing is the #1 threat in Web3 and DeFi. Attackers use homograph attacks to create identical-looking wallet login pages. Our engine detects Punycode and subtle character swaps instantly.
              </p>
              <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span> Detect advanced Homograph attacks
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span> Protect user wallets and assets
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span> Daily automated threat scanning
                </li>
              </ul>
            </div>

            {/* SaaS / Tech */}
            <div className="bg-white dark:bg-dark-800 rounded-2xl p-8 shadow-sm border border-gray-200 dark:border-dark-600 hover:shadow-lg transition-shadow duration-300">
              <div className="flex items-center justify-center w-14 h-14 bg-mint-100 dark:bg-mint-900/30 rounded-xl mb-6">
                <CloudIcon className="h-8 w-8 text-mint-600 dark:text-mint-400" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">SaaS & Startups</h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
                Competitors or malicious actors might register your TLD variations (e.g., .net instead of .com) to steal leads or conduct spear-phishing against your enterprise clients. TypoGuard monitors 1000+ extensions.
              </p>
              <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span> Monitor 1000+ TLDs automatically
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span> Protect employee credentials
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span> Prevent competitor traffic hijacking
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      
      {/* Basic Footer */}
      <footer className="bg-gray-900 dark:bg-dark-950 py-12">
        <div className="text-center text-gray-400">
          <p>&copy; 2025 TypoGuard. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default UseCases;
